import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { ActionType, IAction, IMultiObjectAction } from "UI/UI.Controls/Actions/IAction";
import { IHasRightsEvaluationOverrides } from "../Rights/IHasRightsEvaluationOverrides";
import { StandardAction } from "../UI/XGControls/Actions/StandardAction";
import { StandardRecordAction } from "../UI/XGControls/Actions/StandardRecordAction";
import { IMasterableFrame } from "../UI/XGControls/Frames/FrameInterfaces";
import { IEditableFrame } from "../UI/XGControls/Frames/IEditableFrame";
import { IFrameWithRights, IListFrame } from "../UI/XGControls/IFrame";
import { ComponentTree } from "./ComponentTree";
import { ComponentTreeType } from "./Definitions";
import { ILogicalComponent } from "./ILogicalComponent";
/**
 * Dynamický uzel stromu. Kromě stromu komponent akcí a potomků nalezeném při sestavení stromu,
 * vrací při každém průchodu také dynamický podstrom, který obsahuje nalezené komponenty a standardní řádkové akce
 */
export class DynamicComponentTree extends ComponentTree {
    //#region Private members
    protected override *_getIterator(ct?: CancellationToken): IterableIterator<this> {
        //vrátí komponenty, které byly nalezené při prvním sestavení stromu. Kromě komponent vrací i uzel se standardními akcemi, kromě řádkových akcí, které jsou nalezeny dynamicky (níže v této metodě)
        for (const a of this._children)
            yield a;
        const overrides = this.component.control instanceof IHasRightsEvaluationOverrides ? this.component.control.rightsEvaluationOverrides : undefined;
        //zároveň najde nové podkomponenty
        const tree = DynamicComponentTree.buildTscComponentTree(this.element.control,
            {
                rightsEvaluationOverrides: overrides,
                frameMenuActionsSelector: this._frameMenuRecordActionsSelector,
                skipComponentTreeAssignment: true,
                frameContext: this._buildParameters.frameContext,
            },
            this,
            ct);
        for (const child of (tree.treeNode as DynamicComponentTree)._children) {
            yield child as this;
        }
    }
    //#endregion Private members

    //#region Public members
    /**
     * Podřízené uzly
     *
     */
    public override get children(): Iterable<this> {
        return this._getIterator();
    }
    //#endregion Public members;

    constructor(component: ILogicalComponent, parentNode: ComponentTreeType) {
        super(component, parentNode);
    }

    //#region Private methods
    /**
     * Funkce, která při sestavování stromu vyselektuje akce v menu framu, která mají být přidány do statické části stromu
     */
    protected override _frameMenuActionsSelector(menuAction: IAction, frame: IFrameWithRights): boolean {
        if (menuAction.actionType == ActionType.node) {
            return true;
        }

        if (menuAction instanceof IMultiObjectAction) {
            return menuAction.owner === frame;
        }

        return menuAction instanceof StandardAction && !(menuAction instanceof StandardRecordAction) && menuAction.owner === frame;
    }

    /**
    * Funkce, která při sestavování stromu vyselektuje akce v menu framu, která mají být přidány do dynamické části stromu
    */
    protected _frameMenuRecordActionsSelector(menuAction: IAction, frame: IFrameWithRights): boolean {
        const allowedTypes = [ActionType.node, ActionType.standardMoveLeftRecords, ActionType.standardMoveRightRecords];

        if (allowedTypes.includes(menuAction.actionType))
            return true;

        // If the frame doesn't own the menu, it should accept all actions of other owners.
        // For example when TreeFrame owns actions but rights are evaluated on ListFrame.
        if (frame instanceof IMasterableFrame && frame.isMenuOwner === false) {
            return true;
        }

        if (menuAction instanceof IMultiObjectAction) {
            return menuAction.owner === frame;
        }

        return menuAction instanceof StandardRecordAction && menuAction.owner === frame;
    }

    //#endregion Private methods

    //#region Public methods

    /** Tovární funkce vytvářející speciální uzly stromu    */
    public static createNodeFactory(controlWrapper: ILogicalComponent, parentNode: ComponentTreeType): ComponentTree | undefined {
        if (controlWrapper.control instanceof IListFrame && controlWrapper.control instanceof IEditableFrame)
            return new DynamicComponentTree(controlWrapper, parentNode);
        else
            return undefined;
    }

    //#endregion Public methods
}
